import {
	OnInit, OnDestroy,
	Input, Component, ViewEncapsulation, Output, EventEmitter
} from '@angular/core';
import moment from 'moment';
import { Subscription } from 'rxjs';
import {
	AbstractControl,
	FormArray,
	FormGroup,
	FormControl,
	Validators,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgFor } from '@angular/common';

import { TranslationService } from '../../../../shared/services/translation.service';
import { FormFamilyByVolume, FormFamilyByVolumes } from '../../../models/form-offer';
import { OfferFamilyVolume, DATETIME_FORMAT_PATTERN } from '../../../models/offer';
import { AppWizardService } from '../../../services/app-wizard.service';
import { InputNumberDirective } from '../../../../shared/directives/input-number.directive';

@Component({
	selector: 'copo-offer-pcp-split',
	templateUrl: './offer-pcp-split.component.html',
	styleUrl: './offer-pcp-split.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatFormFieldModule,
		InputNumberDirective,
		MatTooltipModule,
		MatIconModule,
		TranslateModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,
		MatInputModule,
	],
})
export class OfferPcpSplitComponent implements OnInit, OnDestroy {
	@Input() code!: string;
	@Input() formToDate!: Date;
	@Input() formFromDate!: Date;
	@Input() formFamilyByVolumes?: FormFamilyByVolumes;
	@Input() isReadOnly = false;
	@Output() readonly quantityCalculationEvent = new EventEmitter<{ familyCode: string; totalQuantity: number }>();
	formFamilyByVolumeGroup: FormGroup;
	isError = false;
	private readonly subValueChanged?: Subscription;

	constructor(
		readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService
	) {
		this.formFamilyByVolumeGroup = new FormGroup({
			formFamilyByVolumes: this.formFamilyByVolumes || new FormFamilyByVolumes()
		});
	}

	get pcpSplit() {
		return (<FormArray> this.formFamilyByVolumeGroup.controls['formFamilyByVolumes']).controls;
	}

	ngOnInit() {
		this.createPcpSplitObject();
		this.calculateTotalQuantity();
	}

	ngOnDestroy() {
		if (this.subValueChanged) {
			this.subValueChanged.unsubscribe();
		}
	}

	createPcpSplitObject() {
		const formFamilyByVolumeArray = <FormFamilyByVolumes>
			this.formFamilyByVolumeGroup.get('formFamilyByVolumes');
		formFamilyByVolumeArray.clear();

		const stateofoffer = this.appWizardService.getStateOfferForm();
		stateofoffer.getFormFamilyByVolumes().removeFamilyByVolume(this.code);

		// For draft scenario or navigation between steps
		if (this.formFamilyByVolumes && this.formFamilyByVolumes.length > 0) {
			const selectedFBV = this.formFamilyByVolumes.getFormFamilyByVolumes();
			for (const familyByVolume of selectedFBV) {
				formFamilyByVolumeArray.addFormFamilyByVolume(familyByVolume);
				stateofoffer.getFormFamilyByVolumes().addFormFamilyByVolume(familyByVolume);
			}
		} else {
			const pcpType = this.calculatePcPType();
			const count = pcpType === 'M' ? 1 : pcpType === 'Q' ? 3 : 6;
			const offerStartDate = new Date(this.formFromDate);
			let offerEndDate = new Date(this.formToDate);

			// Specific case for OtherPcp adjust month start and end
			if (pcpType !== 'M') {
				const reminderStartMonths = offerStartDate.getMonth() % count;
				if (reminderStartMonths !== 0) {
					offerStartDate.setMonth(offerStartDate.getMonth() - reminderStartMonths);
				}
				const endMonth = (offerEndDate.getMonth() + count)
					- ((offerEndDate.getMonth() + count) % count) - 1;
				offerEndDate = new Date(offerEndDate.getFullYear(), endMonth, 0);
			}
			const noOfMonths = (offerEndDate.getMonth() - offerStartDate.getMonth())
				+ ((offerEndDate.getFullYear() - offerStartDate.getFullYear()) * 12);
			let noOfPcpSplit = noOfMonths + 1;

			if (pcpType === 'Q') {
				noOfPcpSplit = Math.floor((noOfMonths + 3) / 3);
			} else if (pcpType === 'S') {
				noOfPcpSplit = Math.floor((noOfMonths + 6) / 6);
			}

			for (let pcpValue = 1; pcpValue <= noOfPcpSplit; pcpValue++) {
				const startDate = new Date(offerStartDate);
				const endDate = new Date(offerStartDate);

				startDate.setMonth(startDate.getMonth() + ((pcpValue - 1) * count));
				endDate.setMonth(endDate.getMonth() + (pcpValue * count));
				endDate.setDate(endDate.getDate() - 1);
				const familybyvolume: OfferFamilyVolume = {
					familyCode: this.code,
					pcpType,
					pcpValue: String(pcpValue),
					quantityUnit: 't',
					startDate: moment(startDate).format(DATETIME_FORMAT_PATTERN),
					endDate: moment(endDate).format(DATETIME_FORMAT_PATTERN),
				};
				const formFamilyByVolume = new FormFamilyByVolume(familybyvolume);
				formFamilyByVolumeArray.addFormFamilyByVolume(formFamilyByVolume);
				if (this.formFamilyByVolumes) {
					this.formFamilyByVolumes.addFormFamilyByVolume(formFamilyByVolume);
				}
				stateofoffer.getFormFamilyByVolumes().addFormFamilyByVolume(formFamilyByVolume);
			}
		}
	}

	calculatePcPType() {
		const endMonth = this.formToDate.getMonth() + 1;
		const startMonth = this.formFromDate.getMonth() + 1;
		const deltaMonth = endMonth - startMonth + 1
			+ ((this.formToDate.getFullYear() - this.formFromDate.getFullYear()) * 12);
		let pcpType = 'M';
		if (deltaMonth > 16) {
			pcpType = 'S';
		} else if (deltaMonth >= 7) {
			pcpType = 'Q';
		}
		return pcpType;
	}

	getDisplayLabel(control: AbstractControl) {
		const formFamilyByVolumeControl = <FormFamilyByVolume>control;
		const { pcpType, startDate, endDate } = formFamilyByVolumeControl.familybyvolume;
		const lastDate = moment(endDate, DATETIME_FORMAT_PATTERN).toDate();
		const month = lastDate.getMonth() + 1;
		let displaylabel;
		if (pcpType) {
			switch (pcpType) {
			case 'M':
				displaylabel = moment(startDate, DATETIME_FORMAT_PATTERN).format('MMM - YYYY');
				break;
			case 'Q':
				displaylabel = [`${ pcpType }${ Math.floor(month / 3) } - ${ lastDate.getFullYear() }`];
				break;
			case 'S':
				displaylabel = [`${ pcpType }${ Math.floor(month / 6) } - ${ lastDate.getFullYear() }`];
				break;
			}
		}
		return displaylabel;
	}

	calculateTotalQuantity() {
		const familyCode = this.code;
		let totalQuantity = 0;
		const max = 99_999_999.99;
		for (const familByVolume of this.pcpSplit) {
			const quantity = <number | undefined>(<FormFamilyByVolume>familByVolume).getFormQuantity().value;
			if (quantity && quantity >= 0 && quantity <= max) {
				totalQuantity += Number(quantity);
			}
		}
		if (totalQuantity > 0) {
			this.isError = false;
		}
		this.updateQuantityValidators();
		this.quantityCalculationEvent.emit({ familyCode, totalQuantity });
	}

	getFormControlebyIndex(index: number) {
		const controlArray = <FormArray> this.formFamilyByVolumeGroup.get('formFamilyByVolumes');
		return <FormControl>controlArray.at(index).get('quantity');
	}

	getQuantityIndex(index: number) {
		const controlArray = <FormArray> this.formFamilyByVolumeGroup.get('formFamilyByVolumes');
		return <number> controlArray.at(index).get('quantity')?.value || ' ';
	}

	checkQuantity(withFocus: boolean) {
		let totalQuantity = 0;
		this.isError = false;
		const controlArray = <FormArray> this.formFamilyByVolumeGroup.get('formFamilyByVolumes');
		for (let formIndex = 0; formIndex < controlArray.length; formIndex++) {
			const quantityControl = controlArray.at(formIndex).get('quantity');
			if (quantityControl) {
				totalQuantity += Number(quantityControl.value) || 0;
			}
		}
		if (totalQuantity <= 0) {
			this.isError = true;
		}
		this.updateQuantityValidators(withFocus);
		return this.isError ? this.code : '';
	}

	private updateQuantityValidators(withFocus?: boolean) {
		const controlArray = <FormArray> this.formFamilyByVolumeGroup.get('formFamilyByVolumes');
		const quantityControl = <FormControl> controlArray.controls[0].get('quantity');
		if (this.isError) {
			quantityControl.setValidators([
				Validators.min(1),
				Validators.max(99_999_999),
				Validators.required
			]);
			quantityControl.updateValueAndValidity();
			quantityControl.markAsTouched();
			if (withFocus) {
				quantityControl.markAsDirty();
				quantityControl.markAsPending();
				quantityControl.setErrors({ focus: true });
			}
		} else {
			quantityControl.setValidators([Validators.max(99_999_999)]);
			quantityControl.updateValueAndValidity();
			quantityControl.markAsUntouched();
		}
	}
}
